import { DisplayMode } from '../enum';
import {
  Order,
  OrderBundleComponent,
  ToppingsCategoryItem,
} from '../store/order/api/interface';
import { Flavor } from '../store/flavor/api/interface';
import { IsNotNullOrEmpty } from './valid';
import { getAssetURl } from './url';
import { mergeImages } from './mergeImages';

const getFlavorShapeImage = (
  flavor: Flavor | undefined,
  shapeId?: string,
  displayMode?: string
) => {
  if (!flavor?.shapeImages?.length || !shapeId) {
    return null;
  }

  const shapeImage = flavor.shapeImages.find((img) => img.shapeId === shapeId);
  if (!shapeImage) {
    return null;
  }

  return displayMode === DisplayMode.top ? shapeImage.top : shapeImage.front;
};

const getToppingShapeImage = (
  topping: ToppingsCategoryItem | undefined,
  shapeId?: string,
  displayMode?: string
) => {
  if (!topping?.toppingShapeImages?.length || !shapeId) {
    return null;
  }

  const toppingShapeImage = topping.toppingShapeImages.find(
    (img) => img.shapeId === shapeId
  );
  if (!toppingShapeImage) {
    return null;
  }

  return displayMode === DisplayMode.top
    ? toppingShapeImage.top
    : toppingShapeImage.front;
};

export const mergeBundleCakeComponentImage = async (
  component: OrderBundleComponent,
  assetUrl: string
): Promise<string | null> => {
  const cake = component.category_cake;
  const colorCode = cake?.colorCode?.trim();
  if (!cake?.shape || !cake?.flavor || !colorCode) {
    return null;
  }

  const displayMode = cake.displayMode || DisplayMode.front;
  const shapeId = cake.shape.id;

  try {
    if (displayMode === DisplayMode.front) {
      const flavorImage = getFlavorShapeImage(cake.flavor, shapeId, displayMode);
      if (cake.shape.frontImg && flavorImage) {
        return mergeImages(
          getAssetURl(cake.shape.frontImg, assetUrl),
          getAssetURl(flavorImage, assetUrl),
          colorCode,
          getAssetURl(
            getToppingShapeImage(
              cake.topping_category_item,
              shapeId,
              displayMode
            ),
            assetUrl
          ),
          getAssetURl(cake.shape.frontColorImg, assetUrl)
        );
      }
    } else if (displayMode === DisplayMode.top) {
      const flavorImage = getFlavorShapeImage(cake.flavor, shapeId, displayMode);
      if (cake.shape.topImg && flavorImage) {
        return mergeImages(
          getAssetURl(cake.shape.topImg, assetUrl),
          getAssetURl(flavorImage, assetUrl),
          colorCode,
          getAssetURl(
            getToppingShapeImage(
              cake.topping_category_item,
              shapeId,
              displayMode
            ),
            assetUrl
          ),
          getAssetURl(cake.shape.topColorImg, assetUrl)
        );
      }
    }
  } catch (error) {
    console.error('Failed to merge bundle cake component image:', error);
  }

  return null;
};

export const buildBundleComponentMergedImageMap = async (
  order: Order | undefined,
  assetUrl: string
): Promise<Record<string, string | null>> => {
  const components =
    order?.order_bundle_items?.flatMap((item) => item.components ?? []) ?? [];
  const cakeComponents = components.filter(
    (component) => component.componentType === 'cake'
  );

  if (!cakeComponents.length) {
    return {};
  }

  const entries = await Promise.all(
    cakeComponents.map(async (component) => [
      component.id,
      await mergeBundleCakeComponentImage(component, assetUrl),
    ] as const)
  );

  return Object.fromEntries(entries);
};

export const buildOrderMergedImages = async (
  order: Order | undefined,
  assetUrl: string
): Promise<(string | null)[]> => {
  const items = order?.order_items ?? [];
  if (!items.length) {
    return [];
  }

  return Promise.all(
    items.map(async (item) => {
      try {
        if (
          IsNotNullOrEmpty(item?.order_cake) &&
          item?.order_cake?.displayMode === DisplayMode.front
        ) {
          if (
            item?.order_cake?.shape?.frontImg &&
            item?.order_cake?.flavor?.shapeImages?.[0]?.front
          ) {
            return mergeImages(
              getAssetURl(item.order_cake.shape.frontImg, assetUrl),
              getAssetURl(
                item.order_cake.flavor.shapeImages[0].front,
                assetUrl
              ),
              item.order_cake.colorCode,
              getAssetURl(
                item.order_cake.topping_category_item?.toppingShapeImages?.[0]
                  ?.front,
                assetUrl
              ),
              getAssetURl(item.order_cake.shape.frontColorImg, assetUrl)
            );
          }
        } else if (
          IsNotNullOrEmpty(item?.order_cake) &&
          item?.order_cake?.displayMode === DisplayMode.top
        ) {
          if (
            item?.order_cake?.shape?.topImg &&
            item?.order_cake?.flavor?.shapeImages?.[0]?.top
          ) {
            return mergeImages(
              getAssetURl(item.order_cake.shape.topImg, assetUrl),
              getAssetURl(item.order_cake.flavor.shapeImages[0].top, assetUrl),
              item.order_cake.colorCode,
              getAssetURl(
                item.order_cake.topping_category_item?.toppingShapeImages?.[0]
                  ?.top,
                assetUrl
              ),
              getAssetURl(item.order_cake.shape.topColorImg, assetUrl)
            );
          }
        }
      } catch (error) {
        console.error('Failed to merge order item image:', error);
      }

      return null;
    })
  );
};
